import { IStyles } from '../../../commonUtils/styleUtils';
import { IconWrapper } from '../../elements/IconWrapper/Index';
import { ImageWrapper } from '../../elements/ImageWrapper';
import { Loader } from '../../elements/Loader';
import { NoContentCard } from '../NoContent.tsx';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Button, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { PDF_HIDE_CLASS, PRINT_ROOT_CLASS, printReport } from './reportPdf';
import { GIL, gilSize, gilText } from './gilTheme';
import { ReportState } from './useGilReport';

const styles: IStyles = {
  root: {
    width: '100%',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: gilSize(25, 20),
    padding: {
      xs: `${pxToRem(20)} ${pxToRem(20)} ${pxToRem(100)}`,
      md: `${pxToRem(24)} ${pxToRem(20)} ${pxToRem(40)}`,
      lg: `${pxTovW(40)} ${pxTovW(240)}`,
    },
  },
  header: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'flex-start', md: 'center' },
    justifyContent: 'space-between',
    // On a tablet the buttons drop below the title rather than squeeze it.
    flexWrap: { md: 'wrap', lg: 'nowrap' },
    gap: { xs: pxToRem(16), md: pxToRem(18), lg: pxTovW(20) },
  },
  headingBox: {
    display: 'flex',
    flex: { md: `1 1 ${pxToRem(360)}`, lg: '0 1 auto' },
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxToRem(18), lg: pxTovW(20) },
    minWidth: 0,
  },
  headImage: {
    width: { xs: pxToRem(60), md: pxToRem(69), lg: pxTovW(105) },
    height: { xs: pxToRem(60), md: pxToRem(69), lg: pxTovW(105) },
    borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    objectFit: 'cover',
    flexShrink: 0,
  },
  avatar: {
    ...gilText(56, 700, '#111111'),
    width: { xs: pxToRem(64), md: pxToRem(84), lg: pxTovW(130) },
    height: { xs: pxToRem(64), md: pxToRem(84), lg: pxTovW(130) },
    fontSize: { xs: pxToRem(26), md: pxToRem(40), lg: pxTovW(56) },
    borderRadius: { xs: pxToRem(8), md: pxToRem(10), lg: pxTovW(10) },
    backgroundColor: '#E6E3DE',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  avatarCircle: {
    ...gilText(56, 700, '#FFFFFF'),
    width: { xs: pxToRem(64), md: pxToRem(78), lg: pxTovW(120) },
    height: { xs: pxToRem(64), md: pxToRem(78), lg: pxTovW(120) },
    fontSize: { xs: pxToRem(26), md: pxToRem(38), lg: pxTovW(52) },
    borderRadius: '50%',
    backgroundColor: '#007CDC',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  title: {
    ...gilText(36, 700),
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    wordWrap: 'break-word',
  },
  pillRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: gilSize(12, 6),
    paddingTop: gilSize(8, 4),
  },
  pill: {
    display: 'flex',
    alignItems: 'center',
    gap: pxToRem(4),
    padding: {
      xs: `${pxToRem(5)} ${pxToRem(10)}`,
      md: `${pxToRem(6)} ${pxToRem(12)}`, lg: `${pxTovW(6)} ${pxTovW(12)}`,
    },
    borderRadius: gilSize(6, 6),
  },
  pdfButton: {
    // Phones give Download the full row.
    flex: { xs: 1, md: 'none' },
    textTransform: 'none',
    fontFamily: GIL.font,
    fontWeight: 600,
    fontSize: gilSize(13),
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
    color: GIL.green,
    border: `1px solid ${GIL.green}`,
    backgroundColor: 'common.white',
    borderRadius: gilSize(8, 8),
    height: gilSize(52, 40),
    paddingX: gilSize(16, 14),
    '& .MuiButton-startIcon': { marginRight: gilSize(8, 6), marginLeft: 0 },
    '&:hover': {
      backgroundColor: 'common.white',
      border: `1px solid ${GIL.green}`,
    },
  },
  retryButton: {
    textTransform: 'none',
    fontFamily: 'Lato',
    fontWeight: 700,
    color: '#0AA34F',
    border: '1px solid #0AA34F',
    borderRadius: { xs: pxToRem(6), md: pxToRem(6), lg: pxTovW(6) },
    paddingX: { xs: pxToRem(16), md: pxToRem(18), lg: pxTovW(20) },
    '&:hover': { backgroundColor: 'common.white', border: '1px solid #0AA34F' },
  },
};

/** Default tag tint: the design's neutral slate. */
const PILL_COLOR = '#EEF2F6';

export interface MetaItem {
  /** Left out for a pill that is just the value, like "Class 5 A". */
  label?: string;
  value?: string | number;
  iconName?: string;
  /** Tag tint; defaults to the neutral slate. */
  color?: string;
  /** Text colour; defaults to slate grey. */
  textColor?: string;
  /** Semi-bold text, for the tag that leads the row like the class name. */
  bold?: boolean;
}

interface IProps {
  title: string;
  subtitle?: string;
  /** Grey strapline under the title. */
  description?: string;
  showImage?: boolean;
  /** Monogram tile in place of the artwork, like "AS" for a student. */
  avatarText?: string;
  /** Grey rounded tile (default) or the blue circle of the student's own view. */
  avatarVariant?: 'tile' | 'circle';
  meta?: MetaItem[];
  headerRight?: ReactNode;
  state: ReportState<unknown>;
  onRetry?: () => void;
  children?: ReactNode;
}

/**
 * Page frame shared by every GIL report, laid out like the project details and
 * homework report screens: artwork, title, the class line in blue and the key
 * facts as tinted pills. Back navigation comes from the app header.
 */
export const ReportLayout = ({
  title,
  subtitle,
  description,
  showImage = false,
  avatarText,
  avatarVariant = 'tile',
  meta,
  headerRight,
  state,
  onRetry,
  children,
}: IProps) => {
  const visibleMeta = (meta ?? []).filter(
    (item) => item.value !== undefined && item.value !== ''
  );

  return (
    <Box sx={styles.root} className={PRINT_ROOT_CLASS}>
      <Box sx={styles.header}>
        <Box sx={styles.headingBox}>
          {avatarText && (
            <Box
              sx={
                avatarVariant === 'circle' ? styles.avatarCircle : styles.avatar
              }
              aria-hidden
            >
              {avatarText}
            </Box>
          )}
          {showImage && !avatarText && (
            <ImageWrapper
              name="Project_green_icon"
              type="png"
              parentFolder="icons"
              styles={styles.headImage}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: pxToRem(5), md: pxToRem(5), lg: pxTovW(5) },
              minWidth: 0,
            }}
          >
            <Typography sx={styles.title}>{title}</Typography>
            {subtitle && (
              <Typography variant="h5" sx={{ color: '#007CDC' }}>
                {subtitle}
              </Typography>
            )}
            {description && (
              <Typography sx={gilText(16, 400, GIL.muted)}>
                {description}
              </Typography>
            )}
            {visibleMeta.length > 0 && (
              <Box sx={styles.pillRow}>
                {visibleMeta.map((item) => (
                  <Box
                    key={item.label ?? String(item.value)}
                    sx={{
                      ...styles.pill,
                      backgroundColor: item.color ?? PILL_COLOR,
                    }}
                  >
                    {item.iconName && (
                      <IconWrapper
                        name={item.iconName}
                        size="small"
                        type="png"
                        parentFolder="icons"
                      />
                    )}
                    <Typography
                      noWrap
                      sx={gilText(
                        12,
                        item.bold ? 600 : 500,
                        item.textColor ?? GIL.muted
                      )}
                    >
                      {item.label ? `${item.label}: ${item.value}` : item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
        <Box
          className={PDF_HIDE_CLASS}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            gap: { xs: pxToRem(10), md: pxToRem(14), lg: pxTovW(14) },
            width: { xs: '100%', md: 'auto' },
          }}
        >
          {/* On phones the extra action (e.g. the class picker) takes its own
              row, with Download on the row below. */}
          {headerRight && (
            <Box
              sx={{
                display: 'flex',
                flex: { xs: '1 1 100%', md: '0 0 auto' },
                minWidth: 0,
              }}
            >
              {headerRight}
            </Box>
          )}
          {state.status === 'success' && (
            <Button
              variant="outlined"
              sx={styles.pdfButton}
              onClick={printReport}
              startIcon={
                <FileDownloadOutlinedIcon
                  sx={{
                    fontSize: { xs: pxToRem(18), md: pxToRem(20), lg: pxTovW(22) },
                  }}
                />
              }
            >
              Download
            </Button>
          )}
        </Box>
      </Box>

      {state.status === 'loading' && <Loader />}
      {state.status === 'error' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: pxToRem(16), md: pxToRem(18), lg: pxTovW(20) },
          }}
        >
          <NoContentCard variant="error" icon="error" text={state.message} />
          {onRetry && (
            <Button
              variant="outlined"
              sx={styles.retryButton}
              onClick={onRetry}
            >
              Try Again
            </Button>
          )}
        </Box>
      )}
      {state.status === 'success' && children}
    </Box>
  );
};

/** Outlined green header action, matching Download - e.g. "GIL Journey". */
export const HeaderButton = ({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
}) => (
  <Button
    variant="outlined"
    sx={styles.pdfButton}
    onClick={onClick}
    startIcon={icon}
  >
    {label}
  </Button>
);

/** First letters of the first two words: "Aarav Sharma" -> "AS". */
export const initialsOf = (name?: string) =>
  (name ?? '')
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0].toUpperCase())
    .slice(0, 2)
    .join('');

export default ReportLayout;
